import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar, Clock, ArrowRight, Search } from 'lucide-react';
import { brandTheme } from '../styles/BrandTheme';
import HeroSectionComponent from '../components/HeroSection';
import { getAllLondonGuideArticleSummaries } from '../data/serviceAreaBlogMapping';
import { getAllRegionArticleSummaries, getAllTopicArticleSummaries } from '../data/seoArticleLibrary';

const BlogContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const BlogHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 0;

  h1 {
    font-size: clamp(2.5rem, 5vw, 4rem);
    margin-bottom: 1rem;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    font-size: 1.25rem;
    color: ${brandTheme.colors.secondary[600]};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const BlogControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  gap: 2rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 3rem;
    border: 2px solid ${brandTheme.colors.neutral.gray[200]};
    border-radius: ${brandTheme.borderRadius.lg};
    font-size: 1rem;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${brandTheme.colors.primary[500]};
      box-shadow: 0 0 0 3px rgba(254, 206, 27, 0.1);
    }
  }

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${brandTheme.colors.neutral.gray[400]};
  }
`;

const CategoryFilter = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  button {
    padding: 0.5rem 1rem;
    border: 2px solid ${brandTheme.colors.neutral.gray[200]};
    background: white;
    border-radius: ${brandTheme.borderRadius.full};
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    font-weight: 500;

    &.active,
    &:hover {
      background: ${brandTheme.colors.primary[600]};
      color: white;
      border-color: ${brandTheme.colors.primary[600]};
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const FeaturedSection = styled.div`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 2rem;
  color: ${brandTheme.colors.secondary[900]};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, ${brandTheme.colors.primary[500]}, ${brandTheme.colors.primary[700]});
    border-radius: 2px;
  }
`;

const FeaturedArticle = styled.div`
  background: white;
  border-radius: ${brandTheme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  .featured-image {
    background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="2" fill="rgba(59,130,246,0.1)"/></svg>');
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder {
      font-size: 4rem;
      color: ${brandTheme.colors.primary[300]};
    }
  }

  .featured-content {
    padding: 2rem;

    .category {
      display: inline-block;
      background: ${brandTheme.colors.primary[100]};
      color: ${brandTheme.colors.primary[700]};
      padding: 0.25rem 0.75rem;
      border-radius: ${brandTheme.borderRadius.full};
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 1rem;
    }

    h3 {
      font-size: 1.75rem;
      margin-bottom: 1rem;
      line-height: 1.3;
      color: ${brandTheme.colors.secondary[900]};

      &:hover {
        color: ${brandTheme.colors.primary[600]};
      }
    }

    p {
      color: ${brandTheme.colors.secondary[600]};
      margin-bottom: 1.5rem;
      line-height: 1.6;
    }

    .meta {
      display: flex;
      gap: 1rem;
      align-items: center;
      color: ${brandTheme.colors.neutral.gray[500]};
      font-size: 0.9rem;
      margin-bottom: 1.5rem;

      svg {
        width: 16px;
        height: 16px;
      }
    }

    .read-more {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      color: ${brandTheme.colors.primary[600]};
      font-weight: 600;
      text-decoration: none;
      transition: all 0.3s ease;

      &:hover {
        color: ${brandTheme.colors.primary[700]};
        gap: 0.75rem;
      }

      svg {
        transition: transform 0.3s ease;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }
  }
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ArticleCard = styled.div`
  background: white;
  border-radius: ${brandTheme.borderRadius.xl};
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }

  .article-image {
    height: 200px;
    background: linear-gradient(135deg, ${brandTheme.colors.primary[100]}, ${brandTheme.colors.primary[200]});
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="1.5" fill="rgba(59,130,246,0.1)"/></svg>');
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .placeholder {
      font-size: 2.5rem;
      color: ${brandTheme.colors.primary[300]};
    }
  }

  .article-content {
    padding: 1.5rem;

    .category {
      display: inline-block;
      background: ${brandTheme.colors.primary[100]};
      color: ${brandTheme.colors.primary[700]};
      padding: 0.25rem 0.75rem;
      border-radius: ${brandTheme.borderRadius.full};
      font-size: 0.8rem;
      font-weight: 600;
      margin-bottom: 0.75rem;
    }

    h3 {
      font-size: 1.25rem;
      margin-bottom: 0.75rem;
      line-height: 1.4;
      color: ${brandTheme.colors.secondary[900]};

      &:hover {
        color: ${brandTheme.colors.primary[600]};
      }
    }

    p {
      color: ${brandTheme.colors.secondary[600]};
      margin-bottom: 1rem;
      line-height: 1.5;
      font-size: 0.95rem;
    }

    .meta {
      display: flex;
      gap: 1rem;
      align-items: center;
      color: ${brandTheme.colors.neutral.gray[500]};
      font-size: 0.85rem;

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

const NewsletterSection = styled.div`
  background: linear-gradient(135deg, ${brandTheme.colors.primary[600]}, ${brandTheme.colors.primary[800]});
  border-radius: ${brandTheme.borderRadius.xl};
  padding: 3rem;
  text-align: center;
  color: white;
  margin-top: 4rem;

  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: white;
  }

  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }

  .newsletter-form {
    display: flex;
    gap: 1rem;
    max-width: 400px;
    margin: 0 auto;

    @media (max-width: 768px) {
      flex-direction: column;
    }

    input {
      flex: 1;
      padding: 0.75rem 1rem;
      border: none;
      border-radius: ${brandTheme.borderRadius.lg};
      font-size: 1rem;

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
      }
    }

    button {
      padding: 0.75rem 2rem;
      background: white;
      color: ${brandTheme.colors.primary[600]};
      border: none;
      border-radius: ${brandTheme.borderRadius.lg};
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: ${brandTheme.colors.primary[50]};
        transform: translateY(-2px);
      }
    }
  }
`;

const BlogPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'London', 'Regions', 'Driveways', 'Pathways', 'Pool Decks', 'Maintenance', 'Installation', 'Design'];

  const featuredArticle = {
    id: 'ultimate-guide-resin-bound-surfacing',
    title: 'The Ultimate Guide to Resin Bound Surfacing: Everything You Need to Know',
    excerpt: 'Discover everything about resin bound surfacing - from installation process and benefits to maintenance tips and cost considerations. Your complete guide to transforming outdoor spaces.',
    category: 'Installation',
    date: '2025-01-15',
    readTime: '12 min read',
    image: '/images/resin-surfaces-bound-work1.jpeg'
  };

  const articles = [
    {
      id: 'resin-driveway-cost-guide',
      title: 'Resin Driveway Cost Guide: What to Expect in 2025',
      excerpt: 'Complete breakdown of resin driveway costs, including materials, labor, and factors that affect pricing. Get accurate estimates for your project.',
      category: 'Driveways',
      date: '2025-01-12',
      readTime: '8 min read',
      image: '/images/resin-surfaces-bound-work3.jpeg'
    },
    {
      id: 'resin-pathway-design-ideas',
      title: '25 Stunning Resin Pathway Design Ideas for Your Garden',
      excerpt: 'Transform your garden with these beautiful resin pathway designs. From modern minimalist to rustic charm, find inspiration for your outdoor space.',
      category: 'Pathways',
      date: '2025-01-10',
      readTime: '10 min read',
      image: '/images/resin-surfaces-bound-work7.jpeg'
    },
    {
      id: 'pool-deck-surfacing-benefits',
      title: 'Why Resin Bound Pool Decks Are Perfect for Your Swimming Pool',
      excerpt: 'Discover the benefits of resin bound surfacing for pool areas. Safety, durability, and style combined for the perfect pool deck solution.',
      category: 'Pool Decks',
      date: '2025-01-08',
      readTime: '7 min read',
      image: '/images/resin-surfaces-bound-work4.jpeg'
    },
    {
      id: 'resin-surfacing-maintenance-tips',
      title: 'How to Maintain Your Resin Bound Surface: Expert Tips',
      excerpt: 'Keep your resin bound surface looking perfect for years with these professional maintenance tips and cleaning techniques.',
      category: 'Maintenance',
      date: '2025-01-05',
      readTime: '6 min read',
      image: '/images/resin-surfaces-bound-work8.jpeg'
    },
    {
      id: 'resin-vs-concrete-comparison',
      title: 'Resin Bound vs Concrete: Which is Better for Your Driveway?',
      excerpt: 'Comprehensive comparison between resin bound surfacing and traditional concrete. Pros, cons, and which option suits your needs best.',
      category: 'Driveways',
      date: '2025-01-03',
      readTime: '9 min read',
      image: '/images/resin-surfaces-bound-work9.jpeg'
    },
    {
      id: 'resin-bound-installation-process',
      title: 'The Complete Resin Bound Installation Process Explained',
      excerpt: 'Step-by-step guide to resin bound installation. From site preparation to final finishing, understand the entire process.',
      category: 'Installation',
      date: '2025-01-01',
      readTime: '11 min read',
      image: '/images/resin-surfaces-bound-work5.jpeg'
    },
    {
      id: 'diy-vs-professional-installation',
      title: 'DIY vs Professional Resin Bound Installation: Which Should You Choose?',
      excerpt: 'Complete comparison of DIY vs professional resin bound installation. Costs, risks, requirements, and expert advice to help you make the right decision.',
      category: 'Installation',
      date: '2024-12-25',
      readTime: '10 min read',
      image: '/images/resin-surfaces-bound-work2.jpeg'
    },
    {
      id: 'resin-bound-troubleshooting-guide',
      title: 'Resin Bound Troubleshooting Guide: Common Problems and Solutions',
      excerpt: 'Learn how to identify and fix common resin bound surfacing problems. Expert solutions for cracks, discoloration, and drainage issues.',
      category: 'Maintenance',
      date: '2024-12-20',
      readTime: '8 min read',
      image: '/images/resin-surfaces-bound-work6.jpeg'
    },
    {
      id: 'seasonal-care-weather-considerations',
      title: 'Seasonal Care and Weather Considerations for Resin Bound Surfaces',
      excerpt: 'How to protect and maintain your resin bound surface throughout the year. Weather-specific care tips for optimal performance.',
      category: 'Maintenance',
      date: '2024-12-15',
      readTime: '7 min read',
      image: '/images/resin-surfaces-bound-work10.jpeg'
    }
  ];

  const seoTopicSummaries = getAllTopicArticleSummaries();
  const seoRegionSummaries = getAllRegionArticleSummaries();

  const latestTopicArticles = [...articles, ...seoTopicSummaries, ...seoRegionSummaries].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const londonBoroughGuides = getAllLondonGuideArticleSummaries();

  const searchMatch = (title: string, excerpt: string) =>
    searchTerm === '' ||
    title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    excerpt.toLowerCase().includes(searchTerm.toLowerCase());

  const filteredTopicArticles = latestTopicArticles.filter((article) => {
    const matchesSearch = searchMatch(article.title, article.excerpt);
    const matchesCategory =
      activeCategory === 'All' ||
      (activeCategory !== 'London' && article.category === activeCategory);
    return matchesSearch && matchesCategory;
  });

  const filteredLondonArticles = londonBoroughGuides.filter((article) => {
    const matchesSearch = searchMatch(article.title, article.excerpt);
    const matchesCategory = activeCategory === 'All' || activeCategory === 'London';
    return matchesSearch && matchesCategory;
  });

  const showLondonSection = activeCategory === 'All' || activeCategory === 'London';
  const showTopicSection = activeCategory !== 'London';

  const allPostsForSchema = [...londonBoroughGuides, ...articles, ...seoTopicSummaries, ...seoRegionSummaries];

  return (
    <>
      <Helmet>
        <title>Resin Bound Surfacing Blog | Expert Tips & Guides | ZAN RESIN SURFACES LTD</title>
        <meta name="description" content="Expert blog covering resin bound driveways, pathways, and pool decks — plus local guides for every London borough we serve. Installation guides, maintenance tips, design ideas, and cost information from London's leading specialists." />
        <meta name="keywords" content="resin bound blog, London borough guides, resin driveway installation, pathway design, pool deck surfacing, resin maintenance, permeable driveway London, SUDS resin, ZAN Resin Surfaces" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="author" content="ZAN RESIN SURFACES LTD" />
        <link rel="canonical" href="https://www.resinsurfaceslondon.co.uk/blog/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Resin Bound Surfacing Blog | Expert Tips & Guides" />
        <meta property="og:description" content="Expert blog covering resin bound driveways, pathways, and pool decks — plus London borough guides. Installation, maintenance, costs, and design ideas." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.resinsurfaceslondon.co.uk/blog/" />
        <meta property="og:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Resin bound surfacing — ZAN Resin Surfaces London blog" />
        <meta property="og:site_name" content="ZAN RESIN SURFACES LTD" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Resin Bound Surfacing Blog | Expert Tips & Guides" />
        <meta name="twitter:description" content="Expert blog covering resin bound driveways, pathways, and pool decks — plus London borough guides." />
        <meta name="twitter:image" content="https://www.resinsurfaceslondon.co.uk/images/resin-surfaces-bound-work1.jpeg" />
        <meta name="twitter:image:alt" content="Resin bound surfacing — ZAN Resin Surfaces London blog" />
        <meta name="twitter:site" content="@resinsurfaceslondon" />
        <meta name="twitter:creator" content="@resinsurfaceslondon" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Resin Surfaces London Blog",
            "description": "Expert blog covering resin bound surfacing, installation guides, maintenance tips, and London borough local guides",
            "url": "https://www.resinsurfaceslondon.co.uk/blog/",
            "publisher": {
              "@type": "Organization",
              "name": "ZAN RESIN SURFACES LTD",
              "url": "https://www.resinsurfaceslondon.co.uk"
            },
            "blogPost": allPostsForSchema.map(article => ({
              "@type": "BlogPosting",
              "headline": article.title,
              "description": article.excerpt,
              "url": `https://www.resinsurfaceslondon.co.uk/blog/${article.id}/`,
              "datePublished": article.date,
              "author": {
                "@type": "Organization",
                "name": "ZAN RESIN SURFACES LTD"
              },
              "publisher": {
                "@type": "Organization",
                "name": "ZAN RESIN SURFACES LTD"
              }
            }))
          })}
        </script>
      </Helmet>

      <HeroSectionComponent
        title="Resin Bound Surfacing Blog"
        subtitle="Expert tips, guides, and insights for resin bound driveways, pathways, and pool decks. Learn from London's leading specialists."
        badge="Expert Tips • Installation Guides • Maintenance Advice"
        backgroundImage="/images/resin-surfaces-bound-work1.jpeg"
        primaryButtonText="Get Free Quote"
        primaryButtonLink="/contact/"
        secondaryButtonText="Call Now"
        secondaryButtonLink="tel:+447535935779"
      />

      <BlogContainer>
        <BlogHeader>
          <h1>Resin Bound Surfacing Blog</h1>
          <p>
            Expert insights, installation guides, and design inspiration for resin bound driveways, 
            pathways, and pool decks. Stay informed with the latest tips from London's leading specialists.
          </p>
        </BlogHeader>

        <BlogControls>
          <SearchBox>
            <Search size={20} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBox>
          
          <CategoryFilter>
            {categories.map(category => (
              <button
                key={category}
                className={activeCategory === category ? 'active' : ''}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </CategoryFilter>
        </BlogControls>

        <FeaturedSection>
          <SectionTitle>Featured Article</SectionTitle>
          <FeaturedArticle>
            <div className="featured-image">
              <img 
                src={featuredArticle.image} 
                alt={`${featuredArticle.title} - Featured article`}
              />
            </div>
            <div className="featured-content">
              <span className="category">{featuredArticle.category}</span>
              <h3>
                <Link to={`/blog/${featuredArticle.id}/`}>
                  {featuredArticle.title}
                </Link>
              </h3>
              <p>{featuredArticle.excerpt}</p>
              <div className="meta">
                <span>
                  <Calendar size={16} />
                  {new Date(featuredArticle.date).toLocaleDateString('en-GB', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
                <span>
                  <Clock size={16} />
                  {featuredArticle.readTime}
                </span>
              </div>
              <Link to={`/blog/${featuredArticle.id}/`} className="read-more">
                Read Full Article
                <ArrowRight size={16} />
              </Link>
            </div>
          </FeaturedArticle>
        </FeaturedSection>

        {showLondonSection && filteredLondonArticles.length > 0 && (
          <>
            <SectionTitle id="london-borough-guides">London borough guides</SectionTitle>
            <p style={{ textAlign: 'center', maxWidth: '720px', margin: '-1rem auto 2rem', color: brandTheme.colors.secondary[600], lineHeight: 1.6 }}>
              In-depth local guides to resin bound driveways across every London area we serve — linked to our service pages for quotes and surveys.
            </p>
            <ArticlesGrid>
              {filteredLondonArticles.map((article) => (
                <ArticleCard key={article.id}>
                  <div className="article-image">
                    <img src={article.image} alt={article.title} />
                  </div>
                  <div className="article-content">
                    <span className="category">{article.category}</span>
                    <h3>
                      <Link to={`/blog/${article.id}/`}>
                        {article.title}
                      </Link>
                    </h3>
                    <p>{article.excerpt}</p>
                    <div className="meta">
                      <span>
                        <Calendar size={14} />
                        {new Date(article.date).toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span>
                        <Clock size={14} />
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </ArticleCard>
              ))}
            </ArticlesGrid>
          </>
        )}

        {showTopicSection && filteredTopicArticles.length > 0 && (
          <>
            <SectionTitle style={{ marginTop: showLondonSection && filteredLondonArticles.length > 0 ? '3rem' : undefined }}>
              Latest articles
            </SectionTitle>
            <ArticlesGrid>
              {filteredTopicArticles.map((article) => (
                <ArticleCard key={article.id}>
                  <div className="article-image">
                    <img src={article.image} alt={article.title} />
                  </div>
                  <div className="article-content">
                    <span className="category">{article.category}</span>
                    <h3>
                      <Link to={`/blog/${article.id}/`}>
                        {article.title}
                      </Link>
                    </h3>
                    <p>{article.excerpt}</p>
                    <div className="meta">
                      <span>
                        <Calendar size={14} />
                        {new Date(article.date).toLocaleDateString('en-GB', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                      <span>
                        <Clock size={14} />
                        {article.readTime}
                      </span>
                    </div>
                  </div>
                </ArticleCard>
              ))}
            </ArticlesGrid>
          </>
        )}

        {filteredLondonArticles.length === 0 && filteredTopicArticles.length === 0 && (
          <p style={{ textAlign: 'center', padding: '3rem 1rem', color: brandTheme.colors.secondary[600] }}>
            No articles match your search. Try different keywords or clear the category filter.
          </p>
        )}

        <NewsletterSection>
          <h3>Stay Updated</h3>
          <p>
            Get the latest resin bound surfacing tips, design ideas, and industry insights 
            delivered straight to your inbox.
          </p>
          <div className="newsletter-form">
            <input type="email" placeholder="Enter your email address" />
            <button type="submit">Subscribe</button>
          </div>
        </NewsletterSection>
      </BlogContainer>
    </>
  );
};

export default BlogPage;